import { useCallback, useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  DictationController,
  DictationState,
  StartDictationOptions,
} from '../services/DictationController';

/**
 * React surface over the dictation session.
 *
 * The controller owns capture (speech arrives from WakeWordDetector); this hook
 * owns what happens when capture ends — sending the transcript to be structured
 * into note sections and handing the result back to whatever is mounted.
 */

export interface DictationSections {
  [section: string]: string;
}

export interface DictationResult {
  content: string;
  sections: DictationSections;
  /** Clinically meaningful speech that did not map to a section. */
  unassigned: string;
  confidence: number;
  warnings: string[];
  template: string;
}

export interface UseDictationOptions {
  /** Note format to structure into. Defaults to BIRP. */
  template?: string;
  onResult?: (result: DictationResult) => void;
  onError?: (message: string) => void;
}

export function useDictation(options: UseDictationOptions = {}) {
  const { template = 'BIRP', onResult, onError } = options;
  const [state, setState] = useState<DictationState>(DictationController.getState());
  const [result, setResult] = useState<DictationResult | null>(null);

  useEffect(() => DictationController.subscribe(setState), []);

  const structure = useCallback(
    async (transcript: string) => {
      if (!transcript.trim()) {
        DictationController.reset();
        return;
      }

      try {
        const token = await getAuth().currentUser?.getIdToken();
        if (!token) throw new Error('You must be signed in to dictate a note.');

        const response = await fetch('/api/generate-notes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            mode: 'dictation',
            transcript,
            template: DictationController.getState().template || template,
          }),
        });

        if (!response.ok) {
          const detail = await response.text().catch(() => '');
          throw new Error(
            `Could not structure the note (${response.status}). ${detail || 'Please try again.'}`
          );
        }

        const body: DictationResult = await response.json();
        setResult(body);
        onResult?.(body);
        DictationController.finishProcessing();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Dictation could not be processed.';
        // The transcript is preserved in the error state — a failed structuring
        // pass must not silently destroy what the provider just dictated.
        DictationController.failProcessing(message);
        onError?.(message);
      }
    },
    [template, onResult, onError]
  );

  // Register as the processor so a spoken "stop dictation" — which arrives via
  // the speech listener, not this component — ends up on the same path as the
  // stop button.
  useEffect(() => {
    DictationController.setProcessor((transcript) => void structure(transcript));
    return () => DictationController.setProcessor(null);
  }, [structure]);

  const start = useCallback(
    (startOptions: StartDictationOptions = {}) => {
      setResult(null);
      DictationController.start({ template, ...startOptions });
    },
    [template]
  );

  const stop = useCallback(() => {
    DictationController.stop();
  }, []);

  const cancel = useCallback(() => {
    DictationController.cancel();
    setResult(null);
  }, []);

  /** Re-send a transcript that failed to structure, without re-dictating it. */
  const retry = useCallback(() => {
    const { transcript } = DictationController.getState();
    if (transcript.trim()) void structure(transcript);
  }, [structure]);

  return {
    isDictating: state.status === 'listening',
    isProcessing: state.status === 'processing',
    status: state.status,
    transcript: state.transcript,
    segmentCount: state.segments.length,
    startedAt: state.startedAt,
    error: state.error,
    result,
    start,
    stop,
    cancel,
    retry,
  };
}

export default useDictation;
